import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import PaymentPanel from '../../components/payment/PaymentPanel';
import { apiUrl } from '../../services/apiClient';
import { useUserSession } from '../Auth/userSession';
import { fetchPortfolioItems } from '../Portfolio/portfolioService';
import { PortfolioItem } from '../Portfolio/portfolio.types';
import { createDirectOrder, DirectOrderResult } from './checkoutService';
import pageStyles from '../Quote/Quote.module.scss';
import formStyles from '../Quote/QuoteForm.module.scss';
import styles from './Checkout.module.scss';

// 주문상품 상세의 '이 상품으로 주문하기'에서 들어오는 바로결제 페이지.
// 수량과 주문자 정보를 받아 결제 대기 주문을 만든 뒤, 같은 화면에서 결제수단을 골라 결제한다

const MAX_QUANTITY = 1000000;

const checkoutFields = z.object({
  quantity: z
    .number({ invalid_type_error: '수량을 입력해주세요.' })
    .int('수량은 정수로 입력해주세요.')
    .min(1, '수량은 1개 이상이어야 합니다.')
    .max(MAX_QUANTITY, '수량을 확인해주세요.'),
  company: z.string().optional(),
  name: z.string().min(1, '이름을 입력해주세요.'),
  password: z.string().optional(),
  email: z.string().email('이메일 형식이 올바르지 않습니다.'),
  phone1: z.string().regex(/^01[0-9]$/, '연락처를 확인해주세요.'),
  phone2: z.string().regex(/^\d{3,4}$/, '연락처를 확인해주세요.'),
  phone3: z.string().regex(/^\d{4}$/, '연락처를 확인해주세요.'),
  content: z.string().optional(),
  agree: z.boolean().refine((v) => v === true, '개인정보 수집 및 이용에 동의해주세요.'),
});

type CheckoutSchema = z.infer<typeof checkoutFields>;

function buildCheckoutSchema(requirePassword: boolean) {
  return checkoutFields.superRefine((values, ctx) => {
    if (requirePassword && (!values.password || values.password.length < 4)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['password'], message: '비밀번호는 4자 이상 입력해주세요.' });
    }
  });
}

function splitPhone(phone: string): { phone1: string; phone2: string; phone3: string } {
  const digits = phone.replace(/[^0-9]/g, '');
  return {
    phone1: digits.slice(0, 3),
    phone2: digits.length > 7 ? digits.slice(3, digits.length - 4) : digits.slice(3, 7),
    phone3: digits.slice(-4),
  };
}

function Checkout() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [session] = useUserSession();
  const [item, setItem] = useState<PortfolioItem | null | undefined>(undefined);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [order, setOrder] = useState<DirectOrderResult | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPortfolioItems()
      .then((items) => setItem(items.find((candidate) => candidate.id === id) ?? null))
      .catch(() => setItem(null));
  }, [id]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CheckoutSchema>({
    resolver: zodResolver(useMemo(() => buildCheckoutSchema(!session), [session])),
    defaultValues: {
      quantity: 1,
      company: '',
      name: session?.user.name ?? '',
      password: '',
      email: session?.user.email ?? '',
      ...(session?.user.phone ? splitPhone(session.user.phone) : { phone1: '', phone2: '', phone3: '' }),
      content: '',
      agree: false,
    },
  });

  const quantity = watch('quantity');
  const agree = watch('agree');
  const validQuantity = Number.isInteger(quantity) && quantity >= 1 ? quantity : 0;
  const totalPrice = item?.price ? item.price * validQuantity : 0;

  const changeQuantity = (delta: number) => {
    const next = Math.min(MAX_QUANTITY, Math.max(1, (validQuantity || 1) + delta));
    setValue('quantity', next, { shouldValidate: true });
  };

  const goLogin = () => navigate('/login', { state: { from: location.pathname } });

  const onSubmit = async (values: CheckoutSchema) => {
    if (!item) return;
    setSubmitError(null);
    try {
      const result = await createDirectOrder(
        {
          portfolioId: item.id,
          quantity: values.quantity,
          company: values.company ?? '',
          name: values.name,
          password: session ? undefined : values.password,
          email: values.email,
          phone: `${values.phone1}${values.phone2}${values.phone3}`,
          content: values.content ?? '',
        },
        session?.token,
      );
      setOrder(result);
      window.scrollTo(0, 0);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : '주문 접수 중 오류가 발생했습니다.');
    }
  };

  if (item === undefined) {
    return <div className={pageStyles.page} />;
  }

  const crumb = (
    <div className={pageStyles.crumb}>
      <Link to="/">HOME</Link>
      <svg viewBox="0 0 24 24">
        <path d="m9 18 6-6-6-6" />
      </svg>
      <Link to="/order">주문하기</Link>
      <svg viewBox="0 0 24 24">
        <path d="m9 18 6-6-6-6" />
      </svg>
      <span className={pageStyles.crumbHere}>결제하기</span>
    </div>
  );

  // 없는 상품이거나 가격이 등록되지 않은 상품은 바로결제할 수 없다
  if (!item || item.price === null) {
    return (
      <div className={pageStyles.page}>
        <div className={pageStyles.pageHeadWrap}>
          <section className={pageStyles.pageHead}>
            {crumb}
            <h1 className={pageStyles.pageTitle}>바로 결제할 수 없는 상품입니다</h1>
            <p className={pageStyles.pageDesc}>
              상품을 찾을 수 없거나 가격이 정해지지 않은 상품입니다. <Link to="/quote">견적문의</Link>를 이용해주세요.
            </p>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className={pageStyles.page}>
      <div className={pageStyles.pageHeadWrap}>
        <section className={pageStyles.pageHead}>
          {crumb}
          <h1 className={pageStyles.pageTitle}>
            <em>Checkout</em>
          </h1>
          <p className={pageStyles.pageDesc}>
            수량과 주문자 정보를 입력하고 결제를 진행해주세요. 결제가 완료되면 담당자가 1:1 시안을 올려드리며, 시안 수정은
            최대 3회까지 무료입니다.
          </p>
        </section>
      </div>

      <section className={pageStyles.layout}>
        <div className={pageStyles.layoutInner}>
          <div className={pageStyles.main}>
            {/* 주문 상품 */}
            <div className={styles.productCard}>
              <div className={`${styles.productThumb} ${item.thumbnailUrl ? '' : styles[`tone${item.tone}`]}`}>
                {item.thumbnailUrl ? (
                  <img src={apiUrl(item.thumbnailUrl)} alt={item.name} />
                ) : (
                  <span className={styles.productDeco}>{item.deco}</span>
                )}
              </div>
              <div className={styles.productInfo}>
                <span className={styles.productBrand}>
                  {item.code} · {item.brand}
                </span>
                <Link to={`/portfolio/${item.id}`} className={styles.productName}>
                  {item.name}
                </Link>
                <span className={styles.productPrice}>
                  개당 {item.price.toLocaleString('ko-KR')}원{item.priceNote && ` · ${item.priceNote}`}
                </span>
              </div>
            </div>

            {order ? (
              <div className={styles.paySection}>
                <p className={styles.orderNotice}>
                  주문번호 <strong>{order.orderNo}</strong>로 주문서가 접수되었습니다. 결제를 완료하시면 주문이 확정됩니다.
                </p>
                <PaymentPanel
                  orderNo={order.orderNo}
                  orderName={`${item.name} 제작`}
                  amount={order.totalPrice}
                  customerName={watch('name')}
                  description={`${item.name} ${validQuantity.toLocaleString('ko-KR')}개 · 결제수단을 선택하고 결제를 진행해주세요.`}
                />
              </div>
            ) : (
              <>
                {!session && (
                  <div className={pageStyles.estimateGuide}>
                    <strong>비회원으로 주문 중입니다</strong>
                    <p>
                      입력하신 이름 · 연락처 · 주문 비밀번호로 <Link to="/lookup">주문조회</Link>에서 진행 상황을 확인하실 수
                      있습니다. 회원이시면{' '}
                      <button type="button" className={styles.inlineLink} onClick={goLogin}>
                        로그인 후 주문
                      </button>
                      해주세요.
                    </p>
                  </div>
                )}

                <form
                  id="checkoutForm"
                  className={formStyles.form}
                  onSubmit={handleSubmit(onSubmit)}
                  noValidate
                >
                  {/* 수량 */}
                  <section className={formStyles.sectionBlock}>
                    <header className={formStyles.sectionHead}>
                      <h2 className={formStyles.sectionLabel}>
                        수량 <span className={formStyles.req}>*</span>
                      </h2>
                    </header>
                    <div className={styles.quantityRow}>
                      <div className={styles.stepper}>
                        <button type="button" aria-label="수량 줄이기" onClick={() => changeQuantity(-1)}>
                          −
                        </button>
                        <input
                          type="number"
                          min={1}
                          max={MAX_QUANTITY}
                          {...register('quantity', { valueAsNumber: true })}
                        />
                        <button type="button" aria-label="수량 늘리기" onClick={() => changeQuantity(1)}>
                          +
                        </button>
                      </div>
                      <div className={styles.total}>
                        총 결제금액 <strong>{totalPrice.toLocaleString('ko-KR')}원</strong>
                      </div>
                    </div>
                    {errors.quantity && <p className={styles.fieldError}>{errors.quantity.message}</p>}
                  </section>

                  {/* 주문자 정보 */}
                  <section className={formStyles.sectionBlock}>
                    <header className={formStyles.sectionHead}>
                      <h2 className={formStyles.sectionLabel}>
                        주문자 정보 <span className={formStyles.req}>*</span>
                      </h2>
                      <p className={formStyles.sectionHint}>시안 · 배송 안내를 위해 정확히 입력해주세요.</p>
                    </header>
                    <div className={formStyles.fieldGrid}>
                      <div className={formStyles.field}>
                        <label>
                          이름 <span className={formStyles.req}>*</span>
                        </label>
                        <input
                          type="text"
                          className={`${formStyles.textInput} ${errors.name ? formStyles.err : ''}`}
                          placeholder="주문자 이름"
                          {...register('name')}
                        />
                      </div>
                      <div className={formStyles.field}>
                        <label>업체명</label>
                        <input
                          type="text"
                          className={formStyles.textInput}
                          placeholder="회사 / 브랜드명 (선택)"
                          {...register('company')}
                        />
                      </div>
                      {!session && (
                        <div className={formStyles.field}>
                          <label>
                            주문 비밀번호 <span className={formStyles.req}>*</span>
                          </label>
                          <input
                            type="password"
                            className={`${formStyles.textInput} ${errors.password ? formStyles.err : ''}`}
                            placeholder="'주문조회'에서 확인할 때 사용할 비밀번호"
                            {...register('password')}
                          />
                        </div>
                      )}
                      <div className={formStyles.field}>
                        <label>
                          이메일 <span className={formStyles.req}>*</span>
                        </label>
                        <input
                          type="email"
                          className={`${formStyles.textInput} ${errors.email ? formStyles.err : ''}`}
                          placeholder="hello@brand.com"
                          {...register('email')}
                        />
                      </div>
                      <div className={`${formStyles.field} ${formStyles.full}`}>
                        <label>
                          연락처 <span className={formStyles.req}>*</span>
                        </label>
                        <div className={formStyles.phoneRow}>
                          <input
                            type="tel"
                            className={`${formStyles.textInput} ${errors.phone1 ? formStyles.err : ''}`}
                            placeholder="010"
                            maxLength={3}
                            {...register('phone1')}
                          />
                          <input
                            type="tel"
                            className={`${formStyles.textInput} ${errors.phone2 ? formStyles.err : ''}`}
                            placeholder="0000"
                            maxLength={4}
                            {...register('phone2')}
                          />
                          <input
                            type="tel"
                            className={`${formStyles.textInput} ${errors.phone3 ? formStyles.err : ''}`}
                            placeholder="0000"
                            maxLength={4}
                            {...register('phone3')}
                          />
                        </div>
                      </div>
                      <div className={`${formStyles.field} ${formStyles.full}`}>
                        <label>요청사항</label>
                        <textarea
                          className={formStyles.textArea}
                          placeholder="로고 · 문구 등 시안에 반영할 내용을 적어주세요. (선택)"
                          {...register('content')}
                        />
                      </div>
                    </div>
                  </section>

                  {/* 개인정보 동의 */}
                  <section className={formStyles.sectionBlock}>
                    <label className={formStyles.privacyConsent}>
                      <input type="checkbox" {...register('agree')} />
                      주문 처리를 위한 개인정보(이름, 연락처, 이메일) 수집 및 이용에 동의합니다.
                    </label>
                  </section>
                </form>

                {submitError && <p className={styles.fieldError}>{submitError}</p>}
              </>
            )}
          </div>
        </div>
      </section>

      {!order && (
        <div className={formStyles.actionBar}>
          <div className={formStyles.actionInfo}>
            <span>
              총 결제금액 <strong>{totalPrice.toLocaleString('ko-KR')}원</strong>
            </span>
          </div>
          <div className={formStyles.actionButtons}>
            <button
              type="button"
              className={formStyles.btnSecondaryAction}
              onClick={() => navigate(`/portfolio/${item.id}`)}
            >
              취소
            </button>
            <button
              type="submit"
              form="checkoutForm"
              className={formStyles.btnPrimaryAction}
              disabled={!agree || isSubmitting || totalPrice === 0}
            >
              {isSubmitting ? '주문 접수 중...' : '결제하기'}
              <svg viewBox="0 0 24 24">
                <path d="M5 12h14" />
                <path d="m13 6 6 6-6 6" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Checkout;
